import React, { Component } from 'react';
import { Close as CloseIcon, CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import moment from 'moment';
import { colors } from '../theme/index.js';
import TextInput from './TextFields/TextInput.js';
import DateInput from './TextFields/DateInput.js';
import { styled } from '@mui/system';
import * as dates from "@simplybenefits/sb-dates";
import i18n from '../localization/i18n.js';
import tokens from '../localization/tokens.js';
import * as localizationMaps from '../localization/maps.js';
const HoverText = styled("div")({
  fontSize: 13
});
const HoverBox = styled("div")({
  display: "flex",
  flexDirection: "row",
  color: colors.mediumGrey,
  '&:hover': {
    color: colors.mainTextGrey
  }
});
class DatePicker extends Component {
  state = {
    selectedOption: `${this.props.defaultSelectedValue}`,
    customDateRange: false,
    anchorEl: null,
    startDate: dates.getPastDate(dates.getLocalDisplay(), 30, "days", "YYYY/MM/DD", i18n.locale),
    endDate: dates.getLocalDisplay(dates.getLocalDisplay(), "YYYY/MM/DD", i18n.locale),
    startDateError: false,
    endDateError: false
  };
  async componentDidMount() {
    await this._submitDate();
    await this._handleSelectionChange(this.props.defaultSelectedValue);
  }
  anchorElement = /*#__PURE__*/React.createRef();
  _handleInputChange = async (type, value) => {
    await this.setState({
      [type]: value
    });
  };
  _resetDateRange = async () => {
    await this._handleSelectionChange(this.props.defaultSelectedValue);
  };
  _selectPreMade = () => {
    this._handleInputChange("customDateRange", false);
    this._resetDateRange();
  };

  /**
   * Toggle custom date picker
   * @private
   */
  _setCustomDateRange = async () => {
    // reset the errors and toggle modal
    await this.setState({
      customDateRange: !this.state.customDateRange,
      startDateError: false,
      endDateError: false,
      startDate: dates.getPastDate(dates.getLocalDisplay(), 30, "days", "YYYY/MM/DD", i18n.locale),
      endDate: dates.getLocalDisplay(dates.getLocalDisplay(), "YYYY/MM/DD", i18n.locale)
    });
    this._submitDate();
  };

  /**
   * Used in the datepicker to set the parent set to match the current date range
   * @param startDate
   * @param endDate
   */
  _submitDate = () => {
    const dateRange = {
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      error: null
    };
    this.props.submitDate(dateRange);
  };
  _handleSelectionChange = async value => {
    // if the value is stored as french, translate it, otherwise just use the english value
    value = localizationMaps.toEnglish('dateRanges', value) || value;
    if (value === 'Custom Date Range') {
      this._setCustomDateRange();
      return;
    }
    if (value === 'This Year') {
      await this.setState({
        selectedOption: value,
        "startDate": moment().startOf('year'),
        "endDate": moment()
      });
      this._submitDate();
    }
    if (value === 'Last 30 days') {
      await this.setState({
        selectedOption: value,
        "startDate": moment().subtract(30, "days"),
        "endDate": moment()
      });
      this._submitDate();
    }
    if (value === 'Last 60 days') {
      await this.setState({
        selectedOption: value,
        "startDate": moment().subtract(60, "days"),
        "endDate": moment()
      });
      this._submitDate();
    }
    if (value === 'Last 6 months') {
      await this.setState({
        selectedOption: value,
        "startDate": moment().subtract(6, "month"),
        "endDate": moment()
      });
      this._submitDate();
    }
    if (value === 'Last 12 months') {
      await this.setState({
        selectedOption: value,
        "startDate": moment().subtract(12, "month"),
        "endDate": moment()
      });
      this._submitDate();
    }
    if (value === 'Next 30 days') {
      await this.setState({
        selectedOption: value,
        "startDate": moment(),
        "endDate": moment().add(30, "days")
      });
      this._submitDate();
    }
    if (value === 'Next 60 days') {
      await this.setState({
        selectedOption: value,
        "startDate": moment(),
        "endDate": moment().add(60, "days")
      });
      this._submitDate();
    }
    if (value === 'Next 6 months') {
      await this.setState({
        selectedOption: value,
        "startDate": moment(),
        "endDate": moment().add(6, "month")
      });
      this._submitDate();
    }
    if (value === 'Next 12 months') {
      await this.setState({
        selectedOption: value,
        "startDate": moment(),
        "endDate": moment().add(12, "month")
      });
      this._submitDate();
    }
    if (value === 'Today') {
      await this.setState({
        selectedOption: value,
        "startDate": new Date(Number(moment().startOf('day'))),
        "endDate": new Date(Number(moment().endOf('day')))
      });
      this._submitDate();
    }
  };

  /**
   * This will clear the date range
   */
  _clearDateRange = () => {
    this.setState({
      startDate: "YYYY/MM/DD",
      endDate: "YYYY/MM/DD",
      startDateError: false,
      endDateError: false
    });
    const dateRange = {
      error: true
    };
    this.props.submitDate(dateRange);
  };

  /**
   * 
   * @param {*} type 
   * @param {*} value 
   * this will get called onblur for the custom date range, will send the date range to the parent component
   */
  // eslint-disable-next-line consistent-return
  _validateInput = async (type, value, futureDate, noSaveButton) => {
    const dateRange = {
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      error: null
    };

    // if the date is invalid, an error message will be returned
    const isValidDate = dates.validate(value, !futureDate, false, false, i18n.locale);

    // some places this component is used do not have a save button, so we have to handle that with additional logic
    if (noSaveButton) {
      const startDateValid = dates.validate(this.state.startDate, !futureDate, false, false, i18n.locale);
      const endDateValid = dates.validate(this.state.endDate, !futureDate, false, false, i18n.locale);
      if (!startDateValid && !endDateValid) {
        this.props.submitDate(dateRange);
      }
      await this.setState({
        [type]: isValidDate
      });
      return;
    }

    // if an error is returned
    if (isValidDate) {
      await this.setState({
        [type]: isValidDate
      });
      dateRange.error = isValidDate;
      this.props.submitDate(dateRange);
      return;
    }

    // set the errors to false if no error is returned
    await this.setState({
      [type]: false
    });
    this.props.submitDate(dateRange);
  };
  render() {
    const {
      customOptions,
      customStyle,
      futureDate = false,
      noSaveButton = false
    } = this.props;
    const styles = {
      closeModalIcon: {
        cursor: 'pointer',
        display: 'inline',
        fontSize: '16px',
        marginRight: '5px',
        color: colors.mainTextGrey,
        zIndex: '1000'
      },
      calendarIcon: {
        display: 'inline',
        fontSize: '16px',
        marginLeft: '12px',
        color: colors.darkBlue,
        zIndex: '9'
      },
      datePicker: {
        marginTop: '0px',
        color: colors.skyBlue,
        backgroundColor: colors.surface,
        textTransform: 'capitalize',
        '&:hover': {
          color: colors.surface
        },
        ...customStyle
      },
      dateBlock: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%'
      },
      dateField: {
        width: '50%',
        marginTop: '16px',
        marginBottom: '8px'
      },
      inputBlock: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        justifyContent: "space-evenly"
      }
    };

    // showing custom date picker
    if (this.state.customDateRange) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "space-evenly",
          marginBottom: 5
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          ...styles.inputBlock,
          marginRight: 5
        }
      }, /*#__PURE__*/React.createElement(DateInput, {
        label: i18n.t(tokens.labels.dates.dateStart),
        value: this.state.startDate,
        variant: "outlined",
        onChange: e => this._handleInputChange('startDate', e.target.value),
        onBlur: e => this._validateInput('startDateError', e.target.value, futureDate, noSaveButton),
        error: Boolean(this.state.startDateError),
        helperText: this.state.startDateError,
        fullWidth: true,
        customStyle: {
          marginRight: "5px"
        }
      }), /*#__PURE__*/React.createElement(HoverBox, {
        onClick: this._clearDateRange
      }, /*#__PURE__*/React.createElement(CloseIcon, {
        sx: {
          fontSize: 15,
          alignSelf: "center"
        }
      }), /*#__PURE__*/React.createElement(HoverText, null, i18n.t(tokens.labels.dates.clearDates)))), /*#__PURE__*/React.createElement("div", {
        style: styles.inputBlock
      }, /*#__PURE__*/React.createElement(DateInput, {
        label: i18n.t(tokens.labels.dates.dateEnd),
        variant: "outlined",
        value: this.state.endDate,
        onChange: e => this._handleInputChange('endDate', e.target.value),
        onBlur: e => this._validateInput('endDateError', e.target.value, futureDate, noSaveButton),
        error: Boolean(this.state.endDateError),
        helperText: this.state.endDateError,
        fullWidth: true
      }), /*#__PURE__*/React.createElement(HoverBox, {
        onClick: this._selectPreMade
      }, /*#__PURE__*/React.createElement(CalendarTodayIcon, {
        sx: {
          fontSize: 12,
          marginRight: 1,
          alignSelf: "center"
        }
      }), /*#__PURE__*/React.createElement(HoverText, null, i18n.t(tokens.labels.dates.selectDateRange)))));
    }
    const options = customOptions ? [...customOptions] : this.props.futureDate ? ['Next 30 days', 'Next 60 days', 'Next 6 months', 'Next 12 months', 'Custom Date Range'] : ['This Year', 'Last 30 days', 'Last 60 days', 'Last 6 months', 'Last 12 months', 'Custom Date Range'];
    if (this.props.includeToday) {
      options.unshift('Today');
    }
    return /*#__PURE__*/React.createElement(TextInput, {
      id: "DatePicker",
      variant: this.props.variant || "outlined",
      color: "primary",
      "aria-label": "Select Date Range",
      customStyle: styles.datePicker,
      label: i18n.t(tokens.labels.claims.dates),
      fullWidth: true,
      select: true,
      selectOptions: options.map((item, i) => ({
        key: i,
        value: localizationMaps.fromEnglish("dateRanges", item)
      })),
      value: localizationMaps.fromEnglish("dateRanges", this.state.selectedOption),
      onChange: e => this._handleSelectionChange(e.target.value)
    });
  }
}
export default DatePicker;